import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {Map<HTMLDetailsElement, { animation: Animation, expanded: boolean }>} */
  #mobileMenuAnimations = new Map();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#onMobileMenuClick);
    this.#setupAnimatedElementListeners();
    this.querySelectorAll('[data-vela-mobile-menu]').forEach((details) => {
      details.addEventListener('toggle', this.#onMobileMenuToggle);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onMobileMenuClick);
    this.#mobileMenuAnimations.forEach((_, details) => this.#finishMobileMenuAnimation(details));
    this.querySelectorAll('[data-vela-mobile-menu]').forEach((details) => {
      details.removeEventListener('toggle', this.#onMobileMenuToggle);
    });
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    const details = this.#getDetailsElement(event);
    if (details.matches('.vela-mobile-menu__details, .vela-mobile-menu__nested-details')) {
      event.preventDefault();
      this.#animateMobileMenu(details, false);
      details.querySelector('summary')?.focus({ preventScroll: true });
      return;
    }

    this.#close(details);
  };

  /** @param {MouseEvent} event */
  #onMobileMenuClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const summary = event.target.closest('.vela-mobile-menu__summary, .vela-mobile-menu__nested-summary');
    const details = summary?.parentElement;
    if (!(details instanceof HTMLDetailsElement) || summary?.tagName !== 'SUMMARY') return;

    event.preventDefault();
    const expanded = this.#mobileMenuAnimations.get(details)?.expanded ?? details.open;
    this.#animateMobileMenu(details, !expanded);
  };

  /**
   * Animate native details without a fixed maximum height, including image menus.
   * Keep the panel open until a closing animation ends so it can slide upwards.
   * @param {HTMLDetailsElement} details
   * @param {boolean} expanded
   */
  #animateMobileMenu(details, expanded) {
    const summary = details.querySelector(':scope > summary');
    if (!(summary instanceof HTMLElement)) return;

    // A parent with a fixed animated height must not clip a nested menu opening.
    this.#mobileMenuAnimations.forEach((_, other) => {
      if (other !== details && (other.contains(details) || details.contains(other))) {
        this.#finishMobileMenuAnimation(other);
      }
    });

    const startHeight = details.getBoundingClientRect().height;
    this.#finishMobileMenuAnimation(details);
    summary.setAttribute('aria-expanded', String(expanded));
    if (expanded) this.#hydratePromotions(details);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !details.animate) {
      details.open = expanded;
      return;
    }

    details.open = true;
    const endHeight = expanded ? details.getBoundingClientRect().height : summary.getBoundingClientRect().height;
    details.style.overflow = 'hidden';
    const animation = details.animate(
      [{ height: `${startHeight}px` }, { height: `${endHeight}px` }],
      { duration: 500, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'both' }
    );
    this.#mobileMenuAnimations.set(details, { animation, expanded });
    animation.onfinish = () => this.#finishMobileMenuAnimation(details);
  }

  /** @param {HTMLDetailsElement} details */
  #finishMobileMenuAnimation(details) {
    const state = this.#mobileMenuAnimations.get(details);
    if (!state) return;
    state.animation.onfinish = null;
    state.animation.cancel();
    details.style.removeProperty('overflow');
    details.open = state.expanded;
    this.#mobileMenuAnimations.delete(details);
  }

  /** @param {Event} event */
  #onMobileMenuToggle = (event) => {
    if (!(event.currentTarget instanceof HTMLDetailsElement) || !event.currentTarget.open) return;
    this.#hydratePromotions(event.currentTarget);
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  // Individual menu blocks share desktop event attributes with their mobile
  // render. These no-op actions keep delegated focus/pointer events harmless.
  activate() {}

  deactivate() {}

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    if (details === this.refs.details) {
      const openMobileGroup = this.querySelector('[data-vela-mobile-menu][open]');
      if (openMobileGroup instanceof HTMLDetailsElement) this.#hydratePromotions(openMobileGroup);
    }

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    if (details === this.refs.details) {
      this.#mobileMenuAnimations.forEach((_, menu) => this.#finishMobileMenuAnimation(menu));
    }

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Clone promotion cards from inert templates on the first opening of a group.
   * @param {HTMLDetailsElement} details
   */
  #hydratePromotions(details) {
    const host = details.querySelector('.vela-mobile-menu__promotions');
    if (!(host instanceof HTMLElement) || host.hasAttribute('data-promotions-hydrated')) return;

    host.setAttribute('data-promotions-hydrated', '');
    const menuItem = details.closest('.vela-header-menu-item');
    const templates = menuItem?.querySelectorAll('.vela-mega-menu-promo-template') ?? [];

    templates.forEach((template) => {
      if (!(template instanceof HTMLTemplateElement)) return;
      host.append(template.content.cloneNode(true));
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
